import type {InventoryMenuOptions} from '../sm-inventory-context-menu/sm-inventory-context-menu.component';
import type {SmContainerListItemModel} from './sm-container-list-item-model';
import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {buildListItemDescriptionPreview} from '../../text/list-item-description';
import {SmContextMenuItemModel} from '../sm-context-menu-item/sm-context-menu-item.component';
import {SmImagePreviewComponent} from '../sm-image-preview/sm-image-preview.component';

export type {SmContainerListItemModel} from './sm-container-list-item-model';

@Component({
  selector: 'sm-container-list-item',
  templateUrl: './sm-container-list-item.component.html',
  styleUrls: ['./sm-container-list-item.component.scss'],
  standalone: false,
})
export class SmContainerListItemComponent {
  private static readonly dragThresholdPx = 6;

  @Input({required: true}) container!: SmContainerListItemModel;
  @Input() inventoryMenu: InventoryMenuOptions | null = null;
  @Input() menuItems: SmContextMenuItemModel[] = [];
  @Input() emptyDescriptionLabel = '';
  @Input() searchQuery = '';
  @Input() emptyStateLabel = '';
  @Input() hasChildrenStateLabel = '';
  @Input() hasItemsStateLabel = '';
  @Input() menuAriaLabel = '';
  @Input() interactive = true;
  @Input() dragEnabled = false;
  @Input() touchDragFromMainSurface = false;
  @Input() touchDragHoldDelayMs = 0;

  @Output() open = new EventEmitter<SmContainerListItemModel>();
  @Output() menuItemSelect = new EventEmitter<SmContextMenuItemModel>();
  @Output() dragPointerDown = new EventEmitter<PointerEvent>();
  @Output() dragPointerMove = new EventEmitter<PointerEvent>();
  @Output() dragPointerEnd = new EventEmitter<PointerEvent>();
  @Output() dragPointerCancel = new EventEmitter<PointerEvent>();

  @ViewChild(SmImagePreviewComponent) private readonly imagePreview?: SmImagePreviewComponent;

  private dragPointerId: number | null = null;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartedAt = 0;
  private suppressNextOpen = false;

  get description(): string {
    return buildListItemDescriptionPreview(this.container.description);
  }

  get descriptionHtml(): string | null {
    return this.container.description?.trim() || null;
  }

  get hasImage(): boolean {
    return Boolean(this.container.imageUrl);
  }

  get hasKnownContentState(): boolean {
    return typeof this.container.hasChildren === 'boolean' &&
      typeof this.container.hasItems === 'boolean';
  }

  get isDisabled(): boolean {
    return Boolean(this.container.disabled);
  }

  handleOpen(): void {
    if (this.isDisabled || !this.interactive) {
      return;
    }

    this.open.emit(this.container);
  }

  get isSelected(): boolean {
    return Boolean(this.container.selected);
  }

  handleMainClick(event: MouseEvent): void {
    if (this.suppressNextOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.suppressNextOpen = false;
      return;
    }

    this.handleOpen();
  }

  handleMenuItemSelect(item: SmContextMenuItemModel): void {
    this.menuItemSelect.emit(item);
  }

  handleContextMenu(event: MouseEvent): void {
    if (this.dragEnabled && this.touchDragFromMainSurface) {
      event.preventDefault();
    }
  }

  handleDragPointerDown(event: PointerEvent): void {
    if (!this.dragEnabled || !this.canStartDrag(event)) {
      return;
    }

    event.stopPropagation();
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragStartedAt = Date.now();
    this.suppressNextOpen = false;
    this.dragPointerDown.emit(event);
  }

  handleDragPointerMove(event: PointerEvent): void {
    if (!this.dragEnabled || this.dragPointerId !== event.pointerId) {
      return;
    }

    const distance = Math.hypot(event.clientX - this.dragStartX, event.clientY - this.dragStartY);
    if (distance >= SmContainerListItemComponent.dragThresholdPx) {
      this.imagePreview?.closePreview();
      this.suppressNextOpen = true;
    }

    this.dragPointerMove.emit(event);
  }

  handleDragPointerEnd(event: PointerEvent): void {
    if (!this.dragEnabled || this.dragPointerId !== event.pointerId) {
      return;
    }

    if (
      event.pointerType === 'touch'
      && this.touchDragHoldDelayMs > 0
      && Date.now() - this.dragStartedAt >= this.touchDragHoldDelayMs
    ) {
      this.suppressNextOpen = true;
    }

    this.dragPointerEnd.emit(event);
    this.dragPointerId = null;
  }

  handleDragPointerCancel(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) {
      return;
    }

    this.dragPointerCancel.emit(event);
    this.dragPointerId = null;
    this.suppressNextOpen = false;
  }

  preventNativeDrag(event: DragEvent): void {
    event.preventDefault();
  }

  private canStartDrag(event: PointerEvent): boolean {
    if (event.pointerType !== 'touch') {
      return true;
    }

    if (this.touchDragFromMainSurface) {
      return true;
    }

    return event.target instanceof Element && Boolean(
      event.target.closest('.sm-container-list-item__media--touch-drag-source')
    );
  }
}
